/**
 * Preparação da foto no navegador (fase 2): recusa o que o servidor recusaria antes de qualquer upload, reduz para
 * no máximo 2.048 px e reexporta como JPEG. A reexportação pelo canvas descarta o EXIF (inclusive o GPS).
 */

/** `accept` dos seletores de foto: câmera (com `capture`) e galeria. O painel e o "+" usam os mesmos. */
export const CAMERA_ACCEPT = "image/*";
export const GALLERY_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif";

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_SIDE = 2048;
const JPEG_QUALITY = 0.85;

export type PhotoType = "image/jpeg" | "image/png" | "image/webp" | "image/heic";

export class PhotoError extends Error {
  override name = "PhotoError";
}

export const PHOTO_MESSAGES = {
  tooLarge: "A imagem passa de 10 MB. Envie uma foto menor.",
  unsupported: "Formato não aceito. Envie JPEG, PNG, WebP ou HEIC.",
  heicUnsupported: "Formato não suportado neste navegador; envie JPEG ou PNG",
  unreadable: "Não consegui abrir a imagem. Tente outra foto.",
} as const;

const HEIF_BRANDS = new Set(["heic", "heix", "mif1", "heif"]);
const ascii = (bytes: Uint8Array, from: number, to: number) =>
  String.fromCharCode(...bytes.subarray(from, to));

/** Tipo pelos bytes mágicos; HEIC/HEIF pelas marcas `ftyp`. */
export function sniffImageType(bytes: Uint8Array): PhotoType | null {
  if (bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return "image/jpeg";
  if (ascii(bytes, 0, 8) === "\x89PNG\r\n\x1a\n") return "image/png";
  if (ascii(bytes, 0, 4) === "RIFF" && ascii(bytes, 8, 12) === "WEBP") return "image/webp";
  if (ascii(bytes, 4, 8) === "ftyp" && HEIF_BRANDS.has(ascii(bytes, 8, 12))) return "image/heic";
  return null;
}

/** Cabe em `max` px no lado maior, mantendo a proporção; nunca amplia. */
export function fitWithin(width: number, height: number, max = MAX_SIDE) {
  const scale = Math.min(1, max / Math.max(width, height));
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}

export interface Decoded {
  width: number;
  height: number;
  source: CanvasImageSource;
  close?: () => void;
}

/** Decodificar e reexportar ficam injetáveis: o jsdom não tem canvas. */
export interface PrepareDeps {
  decode(file: Blob): Promise<Decoded>;
  encode(source: CanvasImageSource, size: { width: number; height: number }): Promise<PreparedPhoto>;
}

export interface PreparedPhoto {
  blob: Blob;
  /** Pixels da imagem reduzida, para a leitura do QR. */
  imageData: ImageData;
}

export const browserDeps: PrepareDeps = {
  async decode(file) {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    return { width: bitmap.width, height: bitmap.height, source: bitmap, close: () => bitmap.close() };
  },
  async encode(source, { width, height }) {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("canvas indisponível");
    // Fundo branco: PNG transparente não vira preto no JPEG.
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(source, 0, 0, width, height);
    const imageData = ctx.getImageData(0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
    );
    if (!blob) throw new Error("falha ao exportar");
    return { blob, imageData };
  },
};

export async function prepareImage(file: File, deps: PrepareDeps = browserDeps): Promise<PreparedPhoto> {
  // 1. Tamanho e tipo antes de qualquer outra coisa (e de qualquer upload).
  if (file.size > MAX_IMAGE_BYTES) throw new PhotoError(PHOTO_MESSAGES.tooLarge);
  const type = sniffImageType(new Uint8Array(await file.slice(0, 32).arrayBuffer()));
  if (!type) throw new PhotoError(PHOTO_MESSAGES.unsupported);
  // 2. Decodifica, respeitando a orientação da câmera.
  let decoded: Decoded;
  try {
    decoded = await deps.decode(file);
  } catch {
    // 3. HEIC fora do Safari: o navegador não sabe abrir.
    throw new PhotoError(type === "image/heic" ? PHOTO_MESSAGES.heicUnsupported : PHOTO_MESSAGES.unreadable);
  }
  // 4 e 5. Reduz e reexporta como JPEG (sem EXIF).
  try {
    return await deps.encode(decoded.source, fitWithin(decoded.width, decoded.height));
  } catch {
    throw new PhotoError(PHOTO_MESSAGES.unreadable);
  } finally {
    decoded.close?.();
  }
}
